import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Plus, Trash2, UsersRound } from "lucide-react";

import { deleteInvoiceGroup, getInvoiceGroups } from "src/api/invoice";
import { useAuth } from "src/providers/AuthProvider";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import CreateInvoiceGroup from "src/components/modals/CreateInvoiceGroup";
import InvoiceGroupMembers from "src/components/modals/InvoiceGroupMembers";
import { Button } from "src/components/ui/button";
import { DataTable, PageHead, SearchField, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue, showSuccessToast } from "src/lib/utils";

interface InvoiceGroupRow {
	uuid: string;
	name?: string;
}

const InvoiceGroups = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();

	const groupLabel = getCustomizationValue(user, "invoice_group_label", true) || "Invoice Group";
	const groupLabelPlural = getCustomizationValue(user, "invoice_group_label") || "Invoice Groups";
	const invoiceLabel = getCustomizationValue(user, "invoice_label") || "Invoices";

	const search = searchParams.get("search") ?? undefined;
	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);

	const { data, isLoading } = useQuery({
		queryKey: ["invoice-groups", page, limit, search],
		queryFn: () => getInvoiceGroups({ page, limit: limit === "All" ? 9999 : limit, search }),
	});

	const { mutate: deleteGroupMutate } = useMutation(deleteInvoiceGroup, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || `${groupLabel} deleted successfully`);
			queryClient.invalidateQueries(["invoice-groups"]);
		},
	});

	const list = data?.data;
	const groups = list?.data ?? [];

	const columns: Column<InvoiceGroupRow>[] = [
		{ key: "name", header: "Name", primary: true, cell: (row) => <span className="tw-font-medium">{row.name ?? "—"}</span> },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[190px] tw-text-right",
			cell: (row) => (
				<div className="tw-flex tw-justify-end tw-gap-1">
					<CustomModal
						title="Update Members"
						size="normal"
						trigger={
							<button
								type="button"
								className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
							>
								<UsersRound className="tw-h-3.5 tw-w-3.5" />
								Members
							</button>
						}
					>
						<InvoiceGroupMembers uuid={row.uuid} />
					</CustomModal>

					<CustomModal
						title={`Update ${groupLabel}`}
						size="normal"
						trigger={
							<button
								type="button"
								aria-label="Update group"
								className="tw-rounded-lg tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text"
							>
								<Pencil className="tw-h-3.5 tw-w-3.5" />
							</button>
						}
					>
						<CreateInvoiceGroup invoiceGroup={row} />
					</CustomModal>

					<CustomConfirmDialog
						trigger={
							<button
								type="button"
								aria-label="Delete group"
								className="tw-rounded-lg tw-p-1.5 tw-text-red tw-transition-colors hover:tw-bg-red-tint"
							>
								<Trash2 className="tw-h-3.5 tw-w-3.5" />
							</button>
						}
						mutateFn={() => deleteGroupMutate({ uuid: row.uuid })}
					/>
				</div>
			),
		},
	];

	return (
		<div className="tw-space-y-1">
			<PageHead
				title={groupLabelPlural}
				subtitle={`Bill several customers together as one ${groupLabel.toLowerCase()}.`}
				actions={
					<>
						<Link
							to="/invoices"
							className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-3 tw-py-2 tw-text-[12.5px] tw-font-semibold tw-text-text tw-transition-colors hover:tw-bg-bg"
						>
							<ArrowLeft className="tw-h-4 tw-w-4" />
							Back to {invoiceLabel}
						</Link>
						<CustomModal
							title={`Create ${groupLabel}`}
							size="normal"
							trigger={
								<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
									<Plus className="tw-h-4 tw-w-4" />
									Create {groupLabel}
								</Button>
							}
						>
							<CreateInvoiceGroup />
						</CustomModal>
					</>
				}
			/>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-py-4">
				<SearchField placeholder={`Search ${groupLabelPlural.toLowerCase()}...`} />
			</div>

			<DataTable
				data={groups}
				columns={columns}
				rowKey={(row) => row.uuid}
				loading={isLoading}
				totalRows={list?.total ?? groups.length}
				page={page}
				onPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("page", String(next));
					setSearchParams(params);
				}}
				rowsPerPage={limit}
				onRowsPerPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("limit", String(next));
					params.set("page", "1");
					setSearchParams(params);
				}}
				emptyTitle={`No ${groupLabelPlural.toLowerCase()} yet`}
				emptyDescription={`Create a ${groupLabel.toLowerCase()} to bill several customers at once.`}
			/>
		</div>
	);
};

export default InvoiceGroups;
